import React from "react";
import styled from "styled-components";
import {
  FaFacebook,
  FaInstagram,
  FaTiktok,
  FaMapMarkerAlt,
  FaEnvelope,
  FaPhone,
} from "react-icons/fa";

import anti from "../assets/anti.png";
import arvs from "../assets/arvs.png";
import backgroundImage from "../assets/footer-backgrund.jpg";

/* ---------- Layout ---------- */

const FooterWrap = styled.footer`
  background: linear-gradient(rgba(0, 0, 0, 0.55), rgba(0, 0, 0, 0.95)),
    url(${backgroundImage});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;

  color: #fff;
  border-top: 1px solid rgba(255, 255, 255, 0.12);
`;

const Inner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 34px 18px;

  display: grid;
  gap: 22px;
  grid-template-columns: 1.2fr 1.3fr 1fr;

  @media (max-width: 950px) {
    grid-template-columns: 1fr;
    text-align: center;
  }
`;

const Col = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;

  @media (max-width: 950px) {
    align-items: center;
  }
`;

const Brand = styled.div`
  font-size: 20px;
  font-weight: 950;
  letter-spacing: 0.6px;
`;

const Muted = styled.p`
  margin: 0;
  color: rgba(255, 255, 255, 0.78);
  line-height: 1.65;
  font-size: 14px;
  max-width: 420px;

  @media (max-width: 950px) {
    max-width: 520px;
  }
`;

/* ---------- Contact ---------- */

const ContactInfo = styled.div`
  display: grid;
  gap: 10px;

  p {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    margin: 0;
    font-size: 14px;
    color: rgba(255, 255, 255, 0.85);
  }

  svg {
    color: #c37a47;
    font-size: 18px;
    flex: 0 0 auto;
  }

  @media (max-width: 950px) {
    p {
      justify-content: center;
    }
  }
`;

/* ---------- Partner logos ---------- */

const PartnerRow = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;

  @media (max-width: 950px) {
    justify-content: center;
  }
`;

const PartnerLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;

  padding: 10px 12px;
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.14);
  background: rgba(255, 255, 255, 0.06);
  backdrop-filter: blur(6px);

  transition: transform 160ms ease, background 160ms ease;

  &:hover {
    transform: translateY(-2px);
    background: rgba(195, 122, 71, 0.22);

    img {
      filter: grayscale(0%);
      opacity: 1;
      transform: scale(1.05);
    }
  }
`;


const PartnerImg = styled.img`
  width: 120px;
  height: 60px;
  object-fit: contain;
  display: block;

  filter: grayscale(100%);
  opacity: 0.85;
  transition: filter 160ms ease, opacity 160ms ease, transform 160ms ease;

  @media (max-width: 950px) {
    width: 110px;
    height: 55px;
  }
`;

const CodeLink = styled(PartnerLink)`
  img {
    width: 120px;
    height: 60px;
    object-fit: contain;

    filter: grayscale(100%);
    opacity: 0.85;
    transition: filter 160ms ease, opacity 160ms ease, transform 160ms ease;
  }

  &:hover img {
    filter: grayscale(0%);
    opacity: 1;
    transform: scale(1.05);
  }

  @media (max-width: 950px) {
    img {
      width: 110px;
      height: 55px;
    }
  }
`;


/* ---------- Social ---------- */

const SocialWrap = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;

  @media (max-width: 950px) {
    justify-content: center;
  }
`;

const SocialIcon = styled.a`
  width: 44px;
  height: 44px;
  border-radius: 14px;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  color: #fff;
  font-size: 20px;

  border: 1px solid rgba(255, 255, 255, 0.14);
  background: rgba(255, 255, 255, 0.06);
  backdrop-filter: blur(6px);

  transition: transform 160ms ease, background 160ms ease, color 160ms ease;

  &:hover {
    transform: translateY(-2px);
    background: rgba(195, 122, 71, 0.26);
    color: #fff;
  }
`;

/* ---------- Bottom ---------- */

const Bottom = styled.div`
  border-top: 1px solid rgba(255, 255, 255, 0.12);
  padding: 14px 18px;
  text-align: center;
  color: rgba(255, 255, 255, 0.7);
  font-size: 13px;
`;

/* ---------- Component ---------- */

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <FooterWrap>
      <Inner>
        {/* COL 1: Brand + short text */}
        <Col>
          <Brand>Hofors BGK</Brand>
          <Muted>
            Välkommen till vår bangolf i Hofors! Följ oss i sociala medier och
            håll koll på nyheter, tävlingar och öppettider.
          </Muted>

          <SocialWrap>
            <SocialIcon
              href="https://www.facebook.com/hoforsbangolf"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Facebook"
            >
              <FaFacebook />
            </SocialIcon>

            <SocialIcon
              href="https://www.instagram.com/hofors.bangolf/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
            >
              <FaInstagram />
            </SocialIcon>

            <SocialIcon
              href="https://www.tiktok.com/@bangolfklubben.ho"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="TikTok"
            >
              <FaTiktok />
            </SocialIcon>
          </SocialWrap>
        </Col>

        {/* COL 2: Contact */}
        <Col>
          <Brand>Kontakt</Brand>
          <ContactInfo>
            <p>
              <FaMapMarkerAlt /> Skolgatan 21, SE-813 30 Hofors
            </p>
            <p>
              <FaEnvelope /> Hoforsbangolf@gmail.com
            </p>
            <p>
              <FaPhone /> 070-446 33 02
            </p>
          </ContactInfo>
        </Col>

        {/* COL 3: Partners */}
        <Col>
          <Brand>Samarbeten</Brand>

          <PartnerRow>
            <PartnerLink
              href="https://www.antidoping.se/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Anti Doping Sverige"
            >
              <PartnerImg src={anti} alt="Anti Doping Sverige" />
            </PartnerLink>

            <CodeLink
              href="https://codecraftsman.se"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="CodeCraftsMan"
            >
              <img
                src="https://codecraftsman.se/assets/codelogo-CqDVW-XE.svg"
                alt="CodeCraftsMan"
              />
            </CodeLink>

            <PartnerLink
              href="https://www.arvsfonden.se/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Allmänna arvsfonden"
            >
              <PartnerImg src={arvs} alt="Allmänna arvsfonden" />
            </PartnerLink>
          </PartnerRow>
        </Col>
      </Inner>

      <Bottom>© {currentYear} Hofors BGK. Alla rättigheter förbehållna.</Bottom>
    </FooterWrap>
  );
};

export default Footer;






